// People & records stream contracts (module 03 remainders, ADR-023): watchlist screening (PB-03-06),
// sensitive-profile masking (PB-03-07), host directory sync (PB-03-09), record import (PB-03-10) and
// the profile/host field dictionaries (OR-07, OR-08). Messages are @passage/i18n keys (PD-015).
import { z } from 'zod';

const Uuid = z.uuid();
const trimmed = (max: number) => z.string().trim().min(1, 'v.required').max(max, 'v.tooLong');
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, 'v.tooLong')
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const LocalDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'v.date');
const Reason = z.string().trim().min(3, 'ppl.v.reason').max(500, 'v.tooLong');
const FieldKey = z.string().trim().regex(/^[a-z][a-z0-9_]{0,39}$/, 'ppl.v.fieldKey');
const Label = z.object({ en: trimmed(80), ar: z.string().trim().max(80, 'v.tooLong').optional() });

// ------------------------------------------------------------------ screening (PB-03-06)
export const SCREENING_REASON_CODES = ['safety_incident', 'trespass', 'legal_order', 'policy_violation', 'provider_listed', 'other'] as const;
export const SCREENING_DISPOSITIONS = ['false_positive', 'not_relevant', 'confirmed_match'] as const;
export const SCREENING_PROVIDERS = ['simulator'] as const;

/** Per visitor category in the visitor policy (PD-295). */
export const ScreeningCategoryRuleSchema = z.object({ mode: z.enum(['off', 'warn', 'block']), onUnavailable: z.enum(['warn', 'block']).optional() });

export const ScreeningSettingsSchema = z
  .object({
    threshold: z.number().min(0.6).max(1).optional(),
    suppressionDays: z.number().int().min(1).max(1825).optional(),
    providerTimeoutMs: z.number().int().min(250).max(30_000).optional(),
    rescreenHorizonDays: z.number().int().min(1).max(365).optional(),
    rescreenMaxParticipations: z.number().int().min(0).max(5000).optional(),
  })
  .strict();
export const UpdateScreeningSettingsRequest = z.object({ expectedVersion: z.number().int().min(0), settings: ScreeningSettingsSchema });
export interface ScreeningSettingsResponse {
  readonly version: number;
  readonly settings: z.infer<typeof ScreeningSettingsSchema>;
  readonly effective: { readonly threshold: number; readonly suppressionDays: number; readonly providerTimeoutMs: number; readonly rescreenHorizonDays: number; readonly rescreenMaxParticipations: number };
}

/** Synthetic provider list for the simulator adapter (development and tests only). */
export const SimulatorProviderConfig = z
  .object({
    entries: z
      .array(
        z.object({
          ref: z.string().trim().min(1).max(200),
          fullName: trimmed(200),
          aliases: z.array(trimmed(200)).max(10).default([]),
          dateOfBirth: LocalDate.nullable().optional(),
          reasonCode: z.enum(SCREENING_REASON_CODES).default('provider_listed'),
        }),
      )
      .max(2000)
      .default([]),
    unavailable: z.boolean().default(false),
    delayMs: z.number().int().min(0).max(60_000).default(0),
  })
  .strict();

export const CreateScreeningListRequest = z.object({
  siteId: Uuid.nullable(),
  name: trimmed(120),
  kind: z.enum(['deny', 'watch']),
  source: z.enum(['manual', 'provider']),
  provider: z.enum(SCREENING_PROVIDERS).nullable().optional(),
  providerConfig: z.record(z.string(), z.unknown()).optional(),
  provenance: trimmed(500),
});
export const UpdateScreeningListRequest = z.object({
  expectedVersion: z.number().int().min(1),
  name: trimmed(120).optional(),
  provenance: trimmed(500).optional(),
  status: z.enum(['active', 'inactive']).optional(),
  providerConfig: z.record(z.string(), z.unknown()).optional(),
  reason: Reason,
});
export interface ScreeningListResponse {
  readonly id: string;
  readonly siteId: string | null;
  readonly name: string;
  readonly kind: 'deny' | 'watch';
  readonly source: 'manual' | 'provider';
  readonly provider: string | null;
  readonly provenance: string;
  readonly status: 'active' | 'inactive';
  readonly entryCount: number;
  readonly changedAt: string;
  readonly version: number;
}

const Identifier = z.object({ kind: z.enum(['email', 'phone', 'document']), value: trimmed(120) });
const EntryFields = {
  fullName: trimmed(200),
  aliases: z.array(trimmed(200)).max(10).default([]),
  dateOfBirth: LocalDate.nullable().optional(),
  identifier: Identifier.nullable().optional(),
  reasonCode: z.enum(SCREENING_REASON_CODES),
  restrictedNote: optionalText(1000),
  sourceReference: optionalText(200),
  validFrom: LocalDate.nullable().optional(),
  validUntil: LocalDate.nullable().optional(),
};
export const CreateScreeningEntryRequest = z.object(EntryFields);
export const UpdateScreeningEntryRequest = z.object({ ...EntryFields, expectedVersion: z.number().int().min(1), reason: Reason }).partial({ fullName: true, aliases: true, reasonCode: true });
export const RemoveScreeningEntryRequest = z.object({ expectedVersion: z.number().int().min(1), reason: Reason });
export interface ScreeningEntryResponse {
  readonly id: string;
  readonly listId: string;
  readonly fullName: string;
  readonly aliases: readonly string[];
  readonly dateOfBirth: string | null;
  readonly identifier: { readonly kind: 'email' | 'phone' | 'document'; readonly value: string } | null;
  readonly reasonCode: (typeof SCREENING_REASON_CODES)[number];
  readonly restrictedNote: string | null;
  readonly sourceReference: string | null;
  readonly validFrom: string | null;
  readonly validUntil: string | null;
  readonly status: 'active' | 'removed';
  readonly version: number;
  readonly createdAt: string;
}

export const ScreeningCaseQuery = z.object({
  siteId: Uuid,
  status: z.enum(['open', 'cleared', 'confirmed', 'all']).default('open'),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export interface ScreeningCaseSummary {
  readonly id: string;
  readonly siteId: string;
  readonly status: 'open' | 'cleared' | 'confirmed';
  readonly personName: string;
  readonly visit: { readonly id: string; readonly reference: string; readonly title: string | null; readonly startsAt: string | null } | null;
  readonly topScore: number;
  readonly candidateCount: number;
  readonly assignedTo: { readonly id: string; readonly displayName: string } | null;
  readonly openedAt: string;
  readonly version: number;
}
export interface ScreeningCandidateDto {
  readonly listId: string;
  readonly listName: string;
  readonly listKind: 'deny' | 'watch';
  readonly provenance: string;
  readonly source: 'manual' | 'provider';
  readonly entryId: string | null;
  readonly providerRef: string | null;
  readonly entryVersion: number;
  readonly matchedName: string;
  readonly reasonCode: string;
  readonly score: number;
  readonly reasons: readonly string[];
  /** Restricted note of the entry as it is now (null when removed or a provider entry). */
  readonly restrictedNote: string | null;
}
export interface ScreeningCaseDetail extends ScreeningCaseSummary {
  readonly person: { readonly id: string; readonly givenName: string; readonly familyName: string | null; readonly organisation: string | null };
  readonly candidates: readonly ScreeningCandidateDto[];
  readonly disposition: (typeof SCREENING_DISPOSITIONS)[number] | null;
  readonly dispositionReason: string | null;
  readonly restrictedNote: string | null;
  readonly decidedBy: { readonly id: string; readonly displayName: string } | null;
  readonly decidedAt: string | null;
  readonly events: ReadonlyArray<{ readonly kind: string; readonly actor: string | null; readonly at: string; readonly detail: Readonly<Record<string, unknown>> }>;
  /** Earlier cases for the same person at sites the reviewer may see. */
  readonly previousCases: ReadonlyArray<{ readonly id: string; readonly status: 'open' | 'cleared' | 'confirmed'; readonly disposition: string | null; readonly openedAt: string }>;
  /** Why the caller may not decide (separation of duties), if so. */
  readonly decisionBlocked: 'own_visit' | null;
}
export const DecideScreeningCaseRequest = z.object({
  expectedVersion: z.number().int().min(1),
  disposition: z.enum(SCREENING_DISPOSITIONS),
  reason: z.string().trim().min(3, 'ppl.v.reason').max(1000, 'v.tooLong'),
  restrictedNote: optionalText(2000),
});
export const AssignScreeningCaseRequest = z.object({ expectedVersion: z.number().int().min(1), assigneeUserId: Uuid.nullable() });
export const RunScreeningCheckRequest = z.object({ participationId: Uuid });

/** The only screening facts shared outside the restricted module (PD-296). */
export interface ScreeningFactDto {
  readonly state: 'not_applicable' | 'clear' | 'review' | 'confirmed' | 'unavailable';
  readonly checkedAt: string | null;
}

// ------------------------------------------------------------------ field dictionary (OR-07, OR-08)
export const FIELD_TYPE_VALUES = ['text', 'number', 'date', 'boolean', 'select', 'email', 'phone', 'country'] as const;
export const FIELD_CLASS_VALUES = ['standard', 'contact', 'identity', 'restricted'] as const;
const CategoryCode = z.string().trim().regex(/^[a-z][a-z0-9_-]{0,39}$/, 'v.policyKey');
const FieldDefinition = z.object({
  key: FieldKey,
  label: Label,
  help: Label.optional(),
  type: z.enum(FIELD_TYPE_VALUES),
  classification: z.enum(FIELD_CLASS_VALUES),
  active: z.boolean(),
  collect: z.enum(['none', 'all', 'categories']).optional(),
  categories: z.array(CategoryCode).max(30).optional(),
  requiredFor: z.array(CategoryCode).max(30).optional(),
  required: z.boolean().optional(),
  maxLength: z.number().int().min(1).max(500).optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  options: z.array(z.object({ value: z.string().trim().min(1).max(60), label: Label })).max(50).optional(),
});
export const FieldDictionaryDocumentSchema = z.object({
  schema: z.literal(1),
  person: z.array(FieldDefinition).max(40),
  host: z.array(FieldDefinition).max(40),
  revealPurposes: z.array(z.object({ code: FieldKey, label: Label })).min(1).max(20),
  revealSeconds: z.number().int().min(10).max(600),
});
export type FieldDictionaryDocumentInput = z.infer<typeof FieldDictionaryDocumentSchema>;
export const SaveFieldDictionaryDraftRequest = z.object({ expectedVersion: z.number().int().min(0), document: FieldDictionaryDocumentSchema, note: optionalText(500) });
export const PublishFieldDictionaryRequest = z.object({ expectedVersion: z.number().int().min(1) });
export interface FieldDictionaryVersionDto {
  readonly id: string;
  readonly number: number;
  readonly status: 'draft' | 'published' | 'superseded' | 'discarded';
  readonly document: FieldDictionaryDocumentInput;
  readonly note: string | null;
  readonly publishedAt: string | null;
  readonly version: number;
}
export interface FieldDictionaryResponse {
  readonly published: FieldDictionaryVersionDto;
  readonly draft: FieldDictionaryVersionDto | null;
}

export const ProfileFieldValue = z.union([z.string().trim().max(500, 'v.tooLong'), z.number(), z.boolean(), z.null()]);
export const ProfileFieldValues = z.record(FieldKey, ProfileFieldValue);
export const UpdateProfileFieldsRequest = z.object({ values: ProfileFieldValues });
export interface ProfileFieldDto {
  readonly key: string;
  readonly label: string;
  readonly help: string | null;
  readonly type: (typeof FIELD_TYPE_VALUES)[number];
  readonly classification: (typeof FIELD_CLASS_VALUES)[number];
  readonly options: ReadonlyArray<{ readonly value: string; readonly label: string }>;
  /** Null when unset or masked. */
  readonly value: string | number | boolean | null;
  readonly hasValue: boolean;
  readonly masked: boolean;
  /** Safe summary of a masked value (e.g. "•••• 42"). */
  readonly maskedValue: string | null;
  readonly revealable: boolean;
  readonly editable: boolean;
  readonly requiredFor: readonly string[];
  readonly maxLength: number | null;
}
export interface ProfileFieldsResponse {
  readonly dictionaryVersion: number;
  readonly fields: readonly ProfileFieldDto[];
  readonly revealPurposes: ReadonlyArray<{ readonly code: string; readonly label: string }>;
  readonly revealSeconds: number;
  readonly canViewHistory: boolean;
}
export const RevealFieldsRequest = z.object({ fields: z.array(FieldKey).min(1).max(40), purpose: FieldKey, note: optionalText(500) });
export interface RevealFieldsResponse {
  readonly revealId: string;
  readonly values: Readonly<Record<string, string | number | boolean | null>>;
  readonly expiresAt: string;
}
export interface RevealHistoryResponse {
  readonly items: ReadonlyArray<{ readonly id: string; readonly user: string; readonly fields: readonly string[]; readonly purpose: string; readonly note: string | null; readonly at: string; readonly expiresAt: string }>;
}

// ------------------------------------------------------------------ host directory sync (PB-03-09)
export const HOST_SYNC_FIELDS = ['department', 'jobTitle', 'employeeNumber', 'officeSiteCode'] as const;
const AttrName = z.string().trim().min(1).max(100);
export const HostSyncSettingRequest = z.object({
  expectedVersion: z.number().int().min(0),
  enabled: z.boolean(),
  mapping: z.object({ department: AttrName.nullable().optional(), jobTitle: AttrName.nullable().optional(), employeeNumber: AttrName.nullable().optional(), officeSiteCode: AttrName.nullable().optional() }).strict(),
  linkAccounts: z.boolean(),
  reason: Reason,
});
export const RunHostSyncRequest = z.object({ confirmDeactivations: z.boolean().default(false) });
export interface HostSyncRunResponse {
  readonly id: string;
  readonly trigger: 'manual' | 'directory_sync';
  readonly startedAt: string;
  readonly finishedAt: string | null;
  /** 'unknown': the directory timed out; nothing changed (PD-594). */
  readonly outcome: 'succeeded' | 'held' | 'failed' | 'unknown' | null;
  readonly errorCode: string | null;
  readonly counts: { readonly created: number; readonly updated: number; readonly deactivated: number; readonly reactivated: number; readonly unchanged: number; readonly conflicts: number; readonly reassignments: number };
  /** PB-18-07: department reference changes of an HR run (zero for directory runs). */
  readonly departments: { readonly created: number; readonly renamed: number; readonly deactivated: number; readonly reactivated: number };
  readonly conflicts: ReadonlyArray<{ readonly externalId: string | null; readonly kind: string; readonly detail: Readonly<Record<string, unknown>> }>;
}
export interface HostSyncConnectionDto {
  readonly connectionId: string;
  readonly displayName: string;
  readonly kind: 'simulator' | 'ldap';
  readonly connectionStatus: 'active' | 'disabled';
  readonly setting: { readonly enabled: boolean; readonly mapping: Readonly<Record<string, string | null>>; readonly linkAccounts: boolean; readonly version: number } | null;
  readonly ownedHosts: number;
  readonly inactiveHosts: number;
  readonly lastRun: HostSyncRunResponse | null;
  readonly stale: boolean;
}
export interface HostReassignmentDto {
  readonly id: string;
  readonly siteId: string;
  readonly visit: { readonly id: string; readonly reference: string; readonly title: string | null; readonly startsAt: string | null };
  readonly host: { readonly id: string; readonly displayName: string };
  readonly reason: 'host_inactive_directory' | 'host_inactive_manual' | 'host_inactive_hr';
  readonly openedAt: string;
}
export const LinkHostDirectoryRequest = z.object({ expectedVersion: z.number().int().min(1), connectionId: Uuid, externalId: z.string().trim().min(1).max(256), reason: Reason });

// ------------------------------------------------------------------ record import (PB-03-10)
export const IMPORT_KINDS = ['people', 'hosts', 'organisations'] as const;
export const ImportFileRequest = z.object({
  kind: z.enum(IMPORT_KINDS),
  fileName: z.string().trim().min(1).max(200),
  /** Base64 of the CSV (UTF-8) or XLSX file. */
  contentBase64: z.string().min(4).max(8_000_000),
});
export const CreateImportRequest = ImportFileRequest.extend({
  /** Source column header → target field key ("" or absent = ignored). */
  mapping: z.record(z.string().max(200), z.string().max(60)),
});
export interface ImportFieldDto {
  readonly key: string;
  readonly label: string;
  readonly required: boolean;
}
export interface InspectImportResponse {
  readonly format: 'csv' | 'xlsx';
  readonly headers: readonly string[];
  /** Columns that can never be imported (credentials, secrets). */
  readonly blockedHeaders: readonly string[];
  readonly suggestedMapping: Readonly<Record<string, string>>;
  readonly rowCount: number;
  readonly fields: readonly ImportFieldDto[];
}
export interface ImportMatchDto {
  readonly id: string;
  readonly label: string;
  readonly detail: string | null;
  readonly reasons: readonly string[];
}
export interface ImportRowDto {
  readonly rowNumber: number;
  readonly data: Readonly<Record<string, string>>;
  readonly status: 'pending' | 'valid' | 'invalid' | 'duplicate' | 'applied' | 'skipped';
  readonly errors: Readonly<Record<string, string>>;
  readonly matches: readonly ImportMatchDto[];
  readonly decision: 'create' | 'use_existing' | 'update_existing' | 'skip' | null;
  readonly decisionTarget: string | null;
  readonly resultId: string | null;
}
export interface ImportBatchResponse {
  readonly id: string;
  readonly kind: (typeof IMPORT_KINDS)[number];
  readonly status: 'uploaded' | 'validated' | 'applying' | 'applied' | 'discarded';
  readonly fileName: string;
  readonly format: 'csv' | 'xlsx';
  readonly sha256: string;
  readonly headers: readonly string[];
  readonly ignoredHeaders: readonly string[];
  readonly mapping: Readonly<Record<string, string>>;
  readonly applyPolicy: 'all_or_nothing' | 'valid_rows_only' | null;
  readonly counts: { readonly rows: number; readonly valid: number; readonly invalid: number; readonly duplicate: number; readonly undecided: number; readonly applied: number; readonly skipped: number };
  readonly createdAt: string;
  readonly appliedAt: string | null;
  readonly version: number;
}
export const ImportRowsQuery = z.object({
  status: z.enum(['valid', 'invalid', 'duplicate', 'applied', 'skipped', 'all']).default('all'),
  after: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});
export const DecideImportRowRequest = z.object({ decision: z.enum(['create', 'use_existing', 'update_existing', 'skip']), targetId: Uuid.nullable().optional() });
export const ApplyImportRequest = z.object({ expectedVersion: z.number().int().min(1), policy: z.enum(['all_or_nothing', 'valid_rows_only']) });
export interface ApplyImportResponse {
  readonly batch: ImportBatchResponse;
  readonly appliedThisCall: number;
  readonly remaining: number;
}
